/**
 * 類似日検索ダイアログの純ロジック。
 *
 * 選択 state の遷移 (上限付きトグル) と初期 Selected の優先度判定を提供する。
 */
import { create } from '@bufbuild/protobuf'
import type { MessageShape } from '@bufbuild/protobuf'
import {
  SimilarDayCandidateSchema,
  WeekdayCategory,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { isValidCivilDate } from '~/models/civilDate'
import { dayOfWeekLabelJa } from '~/pages/DemandForecastDaily/models/sectionHelpers'

// ===== 選択 state 遷移 =====

/**
 * 候補日のチェック状態をトグルする。
 * - 未選択の場合: 上限未満なら追加、上限到達なら変更なし
 * - 選択済みの場合: 解除
 */
export function toggleDaySelection(
  selected: string[],
  dateStr: string,
  maxCount: number,
): string[] {
  if (selected.includes(dateStr)) {
    // 解除
    return selected.filter((d) => d !== dateStr)
  }
  // 上限チェック
  if (selected.length >= maxCount) {
    return selected
  }
  return [...selected, dateStr]
}

// ===== 初期 Selected 優先度 =====

/**
 * 過去実績ヘッダーのセット済み日付群 or 自動選択結果から初期 Selected を決定する。
 *
 * 優先度1: 過去実績ヘッダーに1列以上の日付がセット済み → セット済み日付群
 * 優先度2: ヘッダー未セット → 自動選択結果
 */
export function resolveInitialSelected(
  existingHeaderDates: string[],
  autoSelected: string[],
): string[] {
  if (existingHeaderDates.length > 0) {
    return existingHeaderDates
  }
  return autoSelected
}

// ===== 曜日セル (曜日1文字 + 色トークン + 祝バッジ、AC-03/AC-04) =====

/** `similarDayWeekdayCell` の戻り値。 */
export type SimilarDayWeekdayCell = {
  /** 曜日 1 文字 (月〜日)。date が undefined の場合は undefined (呼出側が「—」表示)。 */
  label: string | undefined
  /** 色トークンの意味値: 平日=default / 土曜=info / 日祝=negative。 */
  colorToken: 'default' | 'info' | 'negative'
  /**
   * 「祝」バッジの有無。(HOLIDAY かつ日曜以外) または祝日名が非空のとき 'holiday'。
   */
  badge: 'holiday' | null
  /** 「祝」の隣に併記する祝日名。祝日名が空文字なら null。 */
  holidayName: string | null
}

/**
 * 類似日検索ダイアログの候補行の曜日セルを導出する (spec AC-015/AC-016、AC-03/
 * AC-04)。曜日 1 文字は `dayOfWeekLabelJa` (sectionHelpers.ts 既存) へ委譲し
 * 二重導出を避ける。色トークンは weekdayCategory の意味値のみを返す (色そのもの
 * = hex / CSS 値は呼出側の JSX が解決する)。
 *
 * backend `DetermineWeekdayCategory` は holiday-first (SoT 準拠、
 * weekday_category.go 参照): 祝日は曜日にかかわらず HOLIDAY で届くため、
 * 土曜・日曜に重なった祝日も weekdayCategory=HOLIDAY として届く (通常の土曜は
 * SATURDAY)。ただし特異日マスタの需要扱いで曜日区分が WEEKDAY / SATURDAY に
 * 上書きされた祝日 (例: 海の日) は HOLIDAY で届かない。
 *
 * badge は「(HOLIDAY かつ日曜以外) または祝日名が非空」で 'holiday' にし、祝日名が
 * 非空なら holidayName に載せて併記させる (AC-DFD-016「候補日が祝日である場合、
 * 曜日列に『祝』バッジと祝日名を併記」)。祝日名の有無は weekdayCategory に依存しない。
 * 日曜に重なった祝日で名前が無いものは通常の日曜と同じ HOLIDAY で届き判別できない
 * ため出さない (spec Discussion 決定 B、#3806「日曜祝日はバッジ非表示を許容」)。
 * 名前があれば日曜祝日でも判別できるので「祝」と名前を出す。
 */
export function similarDayWeekdayCell(
  date: ProtoDate | undefined,
  weekdayCategory: WeekdayCategory,
  holidayName: string,
): SimilarDayWeekdayCell {
  const label = dayOfWeekLabelJa(date)

  const colorToken: SimilarDayWeekdayCell['colorToken'] =
    weekdayCategory === WeekdayCategory.SATURDAY
      ? 'info'
      : weekdayCategory === WeekdayCategory.HOLIDAY
        ? 'negative'
        : 'default'

  // badge: (HOLIDAY かつ日曜以外) または祝日名が非空。曜日判定は日付から
  // 曜日番号 (Date.getDay(): 日=0, 月=1, ..., 土=6) を直接見る — 表示用ラベル
  // (dayOfWeekLabelJa) の文字列比較に依存すると、ラベル表の変更・i18n 化で
  // バッジ判定が無言で壊れる。
  const dayOfWeek =
    date !== undefined
      ? new Date(date.year, date.month - 1, date.day).getDay()
      : undefined
  const isSunday = dayOfWeek === 0
  const hasHolidayName = holidayName !== ''
  const badge: SimilarDayWeekdayCell['badge'] =
    (weekdayCategory === WeekdayCategory.HOLIDAY &&
      dayOfWeek !== undefined &&
      !isSunday) ||
    hasHolidayName
      ? 'holiday'
      : null

  return {
    label,
    colorToken,
    badge,
    holidayName: hasHolidayName ? holidayName : null,
  }
}

// ===== 候補一覧に無い選択日の行 (AC-DFD-022) =====

/**
 * `YYYY-MM-DD` → ProtoDate。書式不正・非実在日は undefined。
 *
 * 実在判定は [[isValidCivilDate]] に委譲する。ここで `new Date(year, ...)` を
 * 直に往復させると、**年 1〜99 が 1901〜1999 に丸められて有効な日付を弾く**
 * (`new Date(1, 0, 1).getFullYear() === 1901`)。`isValidCivilDate` は
 * `setUTCFullYear` でその丸めを戻しており、手動追加の入口 (`handleAddManual`)
 * もそちらを通す。判定が 2 つに割れていると、**入口は通ったのに行が出ない**
 * という形で選択が見えなくなる (本 helper を足した目的そのものを壊す)。
 */
function parseDateStr(dateStr: string): ProtoDate | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  if (m === null) return undefined
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  if (!isValidCivilDate({ year, month, day })) return undefined
  return { $typeName: 'google.type.Date', year, month, day }
}

/**
 * 選択済みだが候補一覧に**無い**日を、候補行と同じ形で描画するための擬似候補に
 * 変換する (「日付を手動追加」= AC-DFD-022 / 遡る日数の窓外の初期選択日)。
 *
 * これが無いと、候補一覧の外にある日を「追加」しても表に行が出ず、カウンタも
 * 無い (選択状態は候補行のチェックボックスだけで表す設計) ため、**選択できた
 * ことがどこにも出ない**。design (8080) `_addManualSimilar` は
 * 「範囲に無ければ行を先頭に挿入」して selected にしており、その挙動に揃える。
 *
 * 気象・気温・需要・日区分は backend 候補にしか無いので埋めない
 * (呼出側 JSX が undefined / UNSPECIFIED を「—」で描く)。曜日区分だけは
 * **暦の事実である土日のみ**を日付から導く — 祝日はカレンダーから判定できず、
 * 平日扱いで色を塗ると「祝日でない」と誤って主張することになるため
 * UNSPECIFIED のままにする (日区分列は「—」= 不明、と読める)。
 *
 * 並びは `selectedDates` の順 (手動追加は追加順) を保つ。呼出側は候補一覧の
 * **先頭**に置くこと — 一覧は `max-height` 付きのスクロール領域なので、末尾に
 * 足すと結局スクロールしないと見えず、追加した実感が得られない。
 */
export function selectedDatesNotInCandidates(
  selectedDates: string[],
  candidateDateStrs: ReadonlyArray<string | undefined>,
): Array<MessageShape<typeof SimilarDayCandidateSchema>> {
  const known = new Set(candidateDateStrs.filter((d) => d !== undefined))
  return selectedDates
    .filter((dateStr) => !known.has(dateStr))
    .flatMap((dateStr) => {
      const date = parseDateStr(dateStr)
      if (date === undefined) return []
      const dayOfWeek = new Date(date.year, date.month - 1, date.day).getDay()
      const weekdayCategory =
        dayOfWeek === 6
          ? WeekdayCategory.SATURDAY
          : dayOfWeek === 0
            ? WeekdayCategory.HOLIDAY
            : WeekdayCategory.UNSPECIFIED
      return [create(SimilarDayCandidateSchema, { date, weekdayCategory })]
    })
}
